import { useEffect, useMemo, useState } from 'react'
import { Check, ChevronDown, Loader2, SlidersHorizontal } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { cn } from '@/lib/utils'
import { apiService, type LiveTag } from '@/service/api'
import { useLiveReadings } from '@/hooks/useLiveReadings'
import { IntuiLineChart, type ChartConfig } from '@/components/intueri/IntuiLineChart'

/** The most tags a card shows; the server allows 10, but five stay readable. */
export const LIVE_CARD_MAX_TAGS = 5
/** A reading older than this makes the card grey ("stale"). */
export const LIVE_STALE_AFTER_MS = 3 * 60_000

export interface LiveSeriesCardProps {
  documentId: string
  /** Tag ids to show. Omitted: the server picks up to 5 and the picker starts from those. */
  tags?: string[]
  /** `15m` / `1h` / `1d` or an ISO 8601 duration. Default 1 hour. */
  window?: string
  /** Heading, normally the document's name. */
  title?: string
  /** Called with the new tag ids when the user changes the selection in the picker. */
  onTagsChange?: (tags: string[]) => void
  className?: string
}

interface CatalogTag {
  id: string
  label: string
}

const CHART_CONFIG: ChartConfig = { value: { label: 'Value', color: 'hsl(var(--chart-1))' } }

/** Local `HH:MM` for an ISO timestamp; an unparseable value is shown as is. */
function formatClock(value: unknown): string {
  const ms = typeof value === 'string' || typeof value === 'number' ? new Date(value).getTime() : NaN
  return Number.isNaN(ms) ? String(value) : new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
}

function formatValue(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: Math.abs(value) >= 100 ? 1 : 2 }).format(value)
}

/** `Updated 12s ago` / `Updated 3 min ago`, from a ticking `now`. */
function useAgo(since: number | null): number | null {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  return since === null ? null : Math.max(0, Math.round((now - since) / 1000))
}

function useNow(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs)
    return () => clearInterval(id)
  }, [everyMs])
  return now
}

/**
 * The recent readings of a live time-series document: each tag's current value and unit, one line
 * chart of the window for the tag you pick, a Live/stale indicator and an "updated N s ago" line.
 * Plain React with no board or workflow imports, so any surface can mount it.
 */
export function LiveSeriesCard({ documentId, tags, window, title, onTagsChange, className }: LiveSeriesCardProps) {
  const { t } = useTranslation()
  const [localTags, setLocalTags] = useState<string[] | undefined>(tags)
  const requested = tags ?? localTags
  const { data, error, loading, updatedAt } = useLiveReadings(documentId, { tags: requested, window })
  const secondsAgo = useAgo(updatedAt)
  const now = useNow(5000)

  const [focusId, setFocusId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [catalog, setCatalog] = useState<CatalogTag[] | null>(null)
  const [catalogFailed, setCatalogFailed] = useState(false)

  useEffect(() => {
    if (!pickerOpen || catalog || catalogFailed) return
    let cancelled = false
    apiService
      .getTimeseriesTags(documentId)
      .then((res) => {
        if (cancelled) return
        const rows = ((res as { tags?: { tag_id?: string; tag_name?: string }[] })?.tags ?? []).flatMap((r) =>
          r.tag_id ? [{ id: String(r.tag_id), label: r.tag_name ? String(r.tag_name) : String(r.tag_id) }] : []
        )
        setCatalog(rows)
      })
      .catch(() => !cancelled && setCatalogFailed(true))
    return () => {
      cancelled = true
    }
  }, [pickerOpen, catalog, catalogFailed, documentId])

  const shown: LiveTag[] = useMemo(() => data?.tags ?? [], [data])
  const shownIds = shown.map((s) => s.id)
  const focus = shown.find((s) => s.id === focusId) ?? shown.find((s) => s.points.length > 0) ?? shown[0]

  const lastReadingMs = data?.lastReadingAt ? Date.parse(data.lastReadingAt) : null
  const isLive = !!data?.live
  const isFresh = isLive && lastReadingMs !== null && now - lastReadingMs <= LIVE_STALE_AFTER_MS
  const indicator = !data ? null : !isLive ? 'not_live' : isFresh ? 'live' : 'stale'

  const chartData = useMemo(() => (focus?.points ?? []).map((p) => ({ t: p.t, value: p.v })), [focus])
  const chartConfig = useMemo<ChartConfig>(
    () => ({ value: { ...CHART_CONFIG.value, label: focus?.description || focus?.id || '' } }),
    [focus]
  )

  const changeTags = (next: string[]) => {
    setLocalTags(next)
    onTagsChange?.(next)
  }
  const toggleTag = (id: string) => {
    const selected = requested?.length ? requested : shownIds
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]
    if (next.length === 0 || next.length > LIVE_CARD_MAX_TAGS) return
    changeTags(next)
  }
  const selectedForPicker = requested?.length ? requested : shownIds

  const agoLabel =
    secondsAgo === null
      ? null
      : secondsAgo < 60
        ? t('common:live_series.updated_seconds_ago', { n: secondsAgo })
        : t('common:live_series.updated_minutes_ago', { n: Math.floor(secondsAgo / 60) })

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-foreground dark:border-dark-border dark:bg-dark-card',
        className
      )}
      data-testid="live-series-card"
    >
      <header className="flex items-center gap-2 border-b border-gray-200 px-4 py-2 dark:border-dark-border">
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium" title={title}>
          {title || t('common:live_series.untitled')}
        </h3>
        {indicator && (
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 text-xs',
              indicator === 'live' ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-dark-text-muted'
            )}
            data-testid="live-indicator"
            data-state={indicator}
          >
            <span
              className={cn(
                'h-2 w-2 rounded-full',
                indicator === 'live' ? 'animate-pulse bg-emerald-500' : 'bg-gray-400 dark:bg-gray-500'
              )}
            />
            {t(`common:live_series.${indicator}`)}
          </span>
        )}
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        {loading && (
          <div className="flex flex-1 items-center justify-center" role="status" aria-label={t('common:live_series.loading')}>
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}

        {!data && error && (
          <div className="flex flex-1 items-center justify-center text-center text-sm text-destructive" role="alert">
            {t('common:live_series.error')}
          </div>
        )}

        {data && shown.length === 0 && (
          <div className="flex flex-1 items-center justify-center text-center text-sm text-gray-500 dark:text-dark-text-muted">
            {t('common:live_series.empty')}
          </div>
        )}

        {data && shown.length > 0 && (
          <>
            <ul className="flex flex-wrap gap-2" aria-label={t('common:live_series.current_values')}>
              {shown.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setFocusId(s.id)}
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-pressed={focus?.id === s.id}
                    title={s.description ?? s.id}
                    className={cn(
                      'flex min-w-[88px] flex-col rounded-lg border px-3 py-1.5 text-left',
                      focus?.id === s.id
                        ? 'border-brand bg-brand/5 dark:bg-brand/15'
                        : 'border-gray-200 hover:bg-gray-50 dark:border-dark-border dark:hover:bg-dark-muted'
                    )}
                  >
                    <span className="max-w-[140px] truncate text-xs text-gray-500 dark:text-dark-text-muted">{s.id}</span>
                    <span className="text-base font-semibold tabular-nums" data-testid={`live-value-${s.id}`}>
                      {s.latest ? formatValue(s.latest.value) : t('common:live_series.no_value')}
                      {s.latest && s.unit ? <span className="ml-1 text-xs font-normal text-gray-500">{s.unit}</span> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="min-h-[96px] flex-1">
              {chartData.length > 1 ? (
                <IntuiLineChart
                  data={chartData}
                  config={chartConfig}
                  xAxisKey="t"
                  curveType="linear"
                  showXAxis
                  xTickFormatter={formatClock}
                  xMinTickGap={56}
                  showDots
                  dotRadius={2.5}
                  showYAxis
                  className="h-full w-full"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-dark-text-muted">
                  {t('common:live_series.no_points')}
                </div>
              )}
            </div>
          </>
        )}

        {data && error && (
          <p className="text-xs text-amber-600 dark:text-amber-400" role="alert">
            {t('common:live_series.refresh_failed')}
          </p>
        )}
      </div>

      <footer className="border-t border-gray-200 dark:border-dark-border">
        <div className="flex items-center justify-between px-4 py-1.5 text-xs text-gray-500 dark:text-dark-text-muted">
          <span data-testid="live-updated">{agoLabel}</span>
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            onPointerDown={(e) => e.stopPropagation()}
            aria-expanded={pickerOpen}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-gray-100 dark:hover:bg-dark-muted"
          >
            <SlidersHorizontal className="h-3 w-3" />
            {t('common:live_series.tags')}
            <ChevronDown className={cn('h-3 w-3 transition-transform', pickerOpen && 'rotate-180')} />
          </button>
        </div>
        {pickerOpen && (
          // A scroll stays in the list; a pinch (ctrl/⌘+wheel) goes on, so the canvas or board
          // around the card zooms instead of the browser page (FS-723).
          <div
            className="max-h-40 overflow-y-auto border-t border-gray-200 px-2 py-1 dark:border-dark-border"
            onWheelCapture={(e) => {
              if (!e.ctrlKey && !e.metaKey) e.stopPropagation()
            }}
          >
            <p className="px-2 py-1 text-xs text-gray-500 dark:text-dark-text-muted">
              {t('common:live_series.pick_tags', { max: LIVE_CARD_MAX_TAGS })}
            </p>
            {catalogFailed && <p className="px-2 py-1 text-xs text-destructive">{t('common:live_series.tags_failed')}</p>}
            {!catalog && !catalogFailed && <Loader2 className="mx-2 my-1 h-4 w-4 animate-spin text-gray-400" />}
            {catalog?.map((c) => {
              const checked = selectedForPicker.includes(c.id)
              const blocked = (!checked && selectedForPicker.length >= LIVE_CARD_MAX_TAGS) || (checked && selectedForPicker.length === 1)
              return (
                <label
                  key={c.id}
                  className={cn(
                    'flex items-center gap-2 rounded px-2 py-1 text-sm',
                    blocked ? 'opacity-50' : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-muted'
                  )}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <input type="checkbox" checked={checked} disabled={blocked} onChange={() => toggleTag(c.id)} className="sr-only" />
                  <span
                    aria-hidden
                    className={cn(
                      'flex h-4 w-4 items-center justify-center rounded border',
                      checked ? 'border-brand bg-brand text-white' : 'border-gray-300 dark:border-dark-border'
                    )}
                  >
                    {checked && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{c.label}</span>
                </label>
              )
            })}
          </div>
        )}
      </footer>
    </div>
  )
}

export default LiveSeriesCard
